/**
 * React Query hooks for Leads tasks.
 */

import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api, { LeadsCreateTaskInput, LeadsTask } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { toast } from '@/components/ui/use-toast';

interface UseTasksOptions {
  contactId?: string;
  companyId?: string;
  dealId?: string;
  status?: string;
  priority?: string;
  overdue?: boolean;
  sortBy?: string;
  sortDir?: string;
  limit?: number;
}

function buildParams(options: UseTasksOptions): Record<string, string | number | boolean> {
  const params: Record<string, string | number | boolean> = {};
  if (options.contactId) params.contactId = options.contactId;
  if (options.companyId) params.companyId = options.companyId;
  if (options.dealId) params.dealId = options.dealId;
  if (options.status) params.status = options.status;
  if (options.priority) params.priority = options.priority;
  if (options.overdue) params.overdue = true;
  if (options.sortBy) params.sortBy = options.sortBy;
  if (options.sortDir) params.sortDir = options.sortDir;
  if (options.limit !== undefined) params.limit = options.limit;
  return params;
}

export function useTasks(options: UseTasksOptions = {}) {
  const queryClient = useQueryClient();
  const filters = buildParams(options);

  const query = useQuery<{ tasks: LeadsTask[]; total: number }>({
    queryKey: queryKeys.leads.tasks.list(filters),
    queryFn: () => api.listLeadsTasks(Object.keys(filters).length > 0 ? filters : undefined),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: queryKeys.leads.tasks.all() });

  const createMutation = useMutation({
    mutationFn: (input: LeadsCreateTaskInput) => api.createLeadsTask(input),
    onSuccess: invalidate,
    onError: () =>
      toast({ title: 'Error', description: 'Failed to create task', variant: 'destructive' }),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, input }: { id: string; input: Partial<LeadsCreateTaskInput> }) =>
      api.updateLeadsTask(id, input),
    onSuccess: invalidate,
    onError: () =>
      toast({ title: 'Error', description: 'Failed to update task', variant: 'destructive' }),
  });

  const completeMutation = useMutation({
    mutationFn: (id: string) => api.completeLeadsTask(id),
    onSuccess: invalidate,
    onError: () =>
      toast({ title: 'Error', description: 'Failed to complete task', variant: 'destructive' }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deleteLeadsTask(id),
    onSuccess: invalidate,
    onError: () =>
      toast({ title: 'Error', description: 'Failed to delete task', variant: 'destructive' }),
  });

  useEffect(() => {
    if (query.isError && !query.isFetching) {
      toast({ title: 'Error', description: 'Failed to load tasks', variant: 'destructive' });
    }
  }, [query.isError, query.isFetching]);

  return {
    tasks: query.data?.tasks ?? [],
    total: query.data?.total ?? 0,
    loading: query.isLoading,
    refetch: () => query.refetch().then(() => undefined),
    createTask: (input: LeadsCreateTaskInput) => createMutation.mutateAsync(input),
    updateTask: (id: string, input: Partial<LeadsCreateTaskInput>) =>
      updateMutation.mutateAsync({ id, input }),
    completeTask: (id: string) => completeMutation.mutateAsync(id),
    deleteTask: (id: string) => deleteMutation.mutateAsync(id),
  };
}
